import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type BrandingPayload = {
  name: string;
  logoUrl: string | null;
  accentColor: string | null;
};

export function TenantBrandingSettings({ tenantSlug }: { tenantSlug: string }) {
  const t = useTranslations("branding");
  const tAdmin = useTranslations("admin");
  const tRoot = useTranslations();
  const [branding, setBranding] = useState<BrandingPayload | null>(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiFetch("/v1/tenant/branding");
        if (!res.ok) throw await readApiErrorResponse(res);
        const data = (await res.json()).data as BrandingPayload;
        setBranding(data);
        setLogoUrl(data.logoUrl ?? "");
        setAccentColor(data.accentColor ?? "");
      } catch (err) {
        setError(apiErrorText(err, tRoot, "branding.loadFailed", { namespace: "admin.errors" }));
      }
    })();
  }, [tRoot]);

  async function handleSave(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSaved(false);
    const logo = logoUrl.trim();
    // OPS-9: the API only accepts https:// logos; say so before sending.
    if (logo && !/^https:\/\/\S+$/i.test(logo)) {
      setError(tAdmin("brandingLogoHttps"));
      return;
    }
    try {
      const res = await apiFetch("/v1/tenant/branding", {
        method: "PATCH",
        body: JSON.stringify({
          logoUrl: logo ? logo : null,
          accentColor: accentColor.trim() ? accentColor.trim() : null,
        }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      setBranding((await res.json()).data as BrandingPayload);
      setSaved(true);
    } catch (err) {
      setError(apiErrorText(err, tRoot, "branding.saveFailed", { namespace: "admin.errors" }));
    }
  }

  if (!branding && !error) {
    return <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>;
  }

  return (
    <section data-testid="tenant-branding-settings">
      <Link
        to={`/t/${tenantSlug}/admin/members`}
        className="text-sm text-[var(--text-muted)] underline"
      >
        {t("backToAdmin")}
      </Link>
      <h1 className="mt-4 text-2xl font-semibold">{t("title")}</h1>

      <form
        onSubmit={(event) => void handleSave(event)}
        noValidate
        className="mt-6 max-w-lg space-y-4"
      >
        <label className="block text-sm">
          {t("logoUrlLabel")}
          <input
            data-testid="tenant-branding-logo"
            type="url"
            inputMode="url"
            aria-describedby="tenant-branding-logo-hint"
            className="mt-1 w-full rounded border px-3 py-2"
            value={logoUrl}
            onChange={(event) => setLogoUrl(event.target.value)}
            placeholder={t("logoPlaceholder")}
          />
          <span
            id="tenant-branding-logo-hint"
            className="mt-1 block text-xs text-[var(--text-muted)]"
          >
            {tAdmin("brandingLogoHint")}
          </span>
        </label>

        <label className="block text-sm">
          {t("accentColorLabel")}
          <input
            data-testid="tenant-branding-accent"
            className="mt-1 w-full rounded border px-3 py-2"
            value={accentColor}
            onChange={(event) => setAccentColor(event.target.value)}
            placeholder={t("accentPlaceholder")}
          />
        </label>

        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
        {saved ? <p className="text-sm text-green-700">{t("saved")}</p> : null}

        <button
          type="submit"
          data-testid="tenant-branding-save"
          className="rounded bg-[var(--ink-9)] px-4 py-2 text-sm text-white"
        >
          {t("save")}
        </button>
      </form>
    </section>
  );
}
